"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.playerIndexOf = playerIndexOf;
exports.collectRolls = collectRolls;
exports.collectRollsForUser = collectRollsForUser;
exports.diceDistribution = diceDistribution;
exports.chiSquareUniform = chiSquareUniform;
exports.gameDistribution = gameDistribution;
exports.depthStats = depthStats;
exports.listOpponents = listOpponents;
exports.computeHeadToHead = computeHeadToHead;
exports.headToHeadGames = headToHeadGames;
exports.computeUserStats = computeUserStats;
exports.leaderboard = leaderboard;
exports.computeGlobalStats = computeGlobalStats;
exports.engagementStats = engagementStats;
const common_1 = require("../common");
const Model_1 = require("../Model");
const FACES = [1, 2, 3, 4, 5, 6];
const CHI_SQUARE_CRITICAL_5DF = 11.07; // p < 0.05, 5 degrees of freedom
/** WHITE if uid plays white, BLACK if black, -1 if not in the game. */
function playerIndexOf(g, uid) {
    var _a, _b;
    if (((_a = g.players[Model_1.WHITE]) === null || _a === void 0 ? void 0 : _a.uid) === uid)
        return Model_1.WHITE;
    if (((_b = g.players[Model_1.BLACK]) === null || _b === void 0 ? void 0 : _b.uid) === uid)
        return Model_1.BLACK;
    return -1;
}
function collectRolls(games) {
    const rolls = [];
    for (const g of games) {
        for (const snap of g.history) {
            if (snap.dice)
                rolls.push(snap.dice);
        }
    }
    return rolls;
}
function collectRollsForUser(games, uid) {
    const rolls = [];
    for (const g of games) {
        const me = playerIndexOf(g, uid);
        if (me === -1)
            continue;
        for (const snap of g.history) {
            if (snap.dice && snap.turn === me)
                rolls.push(snap.dice);
        }
    }
    return rolls;
}
function diceDistribution(rolls) {
    const faceCounts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 };
    let doubles = 0;
    const categories = {
        normal: 0,
        double: 0,
        oneTwo: 0,
        fiveSix: 0,
    };
    for (const d of rolls) {
        faceCounts[d.die1] += 1;
        faceCounts[d.die2] += 1;
        if ((0, common_1.isDouble)(d)) {
            doubles += 1;
            categories.double += 1;
        }
        else {
            const lo = Math.min(d.die1, d.die2);
            const hi = Math.max(d.die1, d.die2);
            if (lo === 1 && hi === 2)
                categories.oneTwo += 1;
            else if (lo === 5 && hi === 6)
                categories.fiveSix += 1;
            else
                categories.normal += 1;
        }
    }
    return { totalRolls: rolls.length, faceCounts, doubles, categories };
}
function chiSquareUniform(dist) {
    const observed = FACES.map((f) => dist.faceCounts[f]);
    const total = observed.reduce((a, b) => a + b, 0);
    const expected = total / 6;
    const statistic = expected === 0
        ? 0
        : observed.reduce((sum, o) => sum + Math.pow((o - expected), 2) / expected, 0);
    return {
        statistic,
        df: 5,
        critical: CHI_SQUARE_CRITICAL_5DF,
        fair: total === 0 ? true : statistic <= CHI_SQUARE_CRITICAL_5DF,
    };
}
function gameDistribution(games) {
    const byState = {
        created: 0,
        'in-progress': 0,
        'game-over': 0,
    };
    const byRules = { standard: 0, roro: 0 };
    const winType = { normal: 0, gammon: 0, backgammon: 0 };
    const winsByColor = { white: 0, black: 0 };
    for (const g of games) {
        byState[g.gameState.state] += 1;
        byRules[g.rules] += 1;
        if (g.gameState.state === 'game-over') {
            winType[g.gameState.winType] += 1;
            if (g.gameState.winner === Model_1.WHITE)
                winsByColor.white += 1;
            else
                winsByColor.black += 1;
        }
    }
    return { total: games.length, byState, byRules, winType, winsByColor };
}
const ACTIVE_WINDOW_DAYS = 14;
const DAY_MS = 24 * 60 * 60 * 1000;
function dayKey(d) {
    return d.toISOString().slice(0, 10); // YYYY-MM-DD (UTC)
}
function bucketByDay(dates) {
    var _a;
    const map = new Map();
    for (const d of dates) {
        const k = dayKey(d);
        map.set(k, ((_a = map.get(k)) !== null && _a !== void 0 ? _a : 0) + 1);
    }
    return [...map.entries()]
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => a.date.localeCompare(b.date));
}
/**
 * Earliest joinedAt seen for each gameId across all users (≈ game creation
 * time), restricted to `allowedIds` so the games/day series tracks whatever
 * game-type filter produced the games passed to `engagementStats`.
 */
function gameCreationDates(users, allowedIds) {
    const map = new Map();
    for (const u of users) {
        for (const gameId of Object.keys(u.games)) {
            if (!allowedIds.has(gameId))
                continue;
            const info = u.games[gameId];
            if (!info)
                continue;
            const existing = map.get(gameId);
            if (!existing || info.joinedAt.getTime() < existing.getTime()) {
                map.set(gameId, info.joinedAt);
            }
        }
    }
    return Array.from(map.values());
}
const LENGTH_BUCKETS = ['0-20', '21-40', '41-60', '61-80', '81+'];
function rollCount(g) {
    return g.history.reduce((n, s) => n + (s.dice ? 1 : 0), 0);
}
function lengthBucketOf(rolls) {
    if (rolls <= 20)
        return '0-20';
    if (rolls <= 40)
        return '21-40';
    if (rolls <= 60)
        return '41-60';
    if (rolls <= 80)
        return '61-80';
    return '81+';
}
function depthStats(games) {
    var _a;
    const finished = games.filter((g) => g.gameState.state === 'game-over');
    const rolls = finished.map(rollCount);
    const total = rolls.reduce((a, b) => a + b, 0);
    const byRules = {
        standard: { finishedGames: 0, avgRolls: 0 },
        roro: { finishedGames: 0, avgRolls: 0 },
    };
    const ruleTotals = { standard: 0, roro: 0 };
    finished.forEach((g, i) => {
        var _a;
        byRules[g.rules].finishedGames += 1;
        ruleTotals[g.rules] += (_a = rolls[i]) !== null && _a !== void 0 ? _a : 0;
    });
    for (const r of ['standard', 'roro']) {
        const n = byRules[r].finishedGames;
        byRules[r].avgRolls = n === 0 ? 0 : ruleTotals[r] / n;
    }
    const counts = new Map(LENGTH_BUCKETS.map((b) => [b, 0]));
    for (const r of rolls) {
        const b = lengthBucketOf(r);
        counts.set(b, ((_a = counts.get(b)) !== null && _a !== void 0 ? _a : 0) + 1);
    }
    const lengthHistogram = LENGTH_BUCKETS.map((bucket) => {
        var _a;
        return ({
            bucket,
            count: (_a = counts.get(bucket)) !== null && _a !== void 0 ? _a : 0,
        });
    });
    return {
        finishedGames: finished.length,
        avgRolls: finished.length === 0 ? 0 : total / finished.length,
        byRules,
        lengthHistogram,
    };
}
function emptyWinTypeSplit() {
    return {
        win: { normal: 0, gammon: 0, backgammon: 0 },
        lose: { normal: 0, gammon: 0, backgammon: 0 },
    };
}
function gamesForUser(games, uid) {
    return games.filter((g) => playerIndexOf(g, uid) !== -1);
}
function listOpponents(games, uid) {
    var _a;
    const map = new Map();
    for (const g of games) {
        const me = playerIndexOf(g, uid);
        if (me === -1)
            continue;
        const opp = g.players[me === Model_1.WHITE ? Model_1.BLACK : Model_1.WHITE];
        if (!opp)
            continue;
        const entry = (_a = map.get(opp.uid)) !== null && _a !== void 0 ? _a : {
            uid: opp.uid,
            displayName: opp.displayName,
            photoURL: opp.photoURL,
            games: 0,
            wins: 0,
            losses: 0,
        };
        entry.games += 1;
        if (g.gameState.state === 'game-over') {
            if (g.gameState.winner === me)
                entry.wins += 1;
            else
                entry.losses += 1;
        }
        map.set(opp.uid, entry);
    }
    return [...map.values()].sort((x, y) => y.games - x.games);
}
function computeHeadToHead(games, uid, opponentUid) {
    const result = {
        opponentUid,
        games: 0,
        wins: 0,
        losses: 0,
        winType: emptyWinTypeSplit(),
    };
    for (const g of games) {
        const me = playerIndexOf(g, uid);
        const opp = playerIndexOf(g, opponentUid);
        if (me === -1 || opp === -1 || me === opp)
            continue;
        result.games += 1;
        if (g.gameState.state === 'game-over') {
            if (g.gameState.winner === me) {
                result.wins += 1;
                result.winType.win[g.gameState.winType] += 1;
            }
            else {
                result.losses += 1;
                result.winType.lose[g.gameState.winType] += 1;
            }
        }
    }
    return result;
}
/**
 * The finished games played between two users, as flat rows (winner uid +
 * win type). Order matches `games`; callers layer on dates/sorting. Skips
 * unfinished games and any game not involving both players.
 */
function headToHeadGames(games, a, b) {
    const rows = [];
    for (const g of games) {
        const ia = playerIndexOf(g, a);
        const ib = playerIndexOf(g, b);
        if (ia === -1 || ib === -1 || ia === ib)
            continue;
        if (g.gameState.state !== 'game-over')
            continue;
        const winner = g.players[g.gameState.winner];
        if (!winner)
            continue;
        rows.push({
            gameId: g.gameId,
            rules: g.rules,
            winnerUid: winner.uid,
            winType: g.gameState.winType,
        });
    }
    return rows;
}
function computeUserStats(allGames, uid, displayName) {
    const mine = gamesForUser(allGames, uid);
    const winType = emptyWinTypeSplit();
    const byRules = {
        standard: { wins: 0, losses: 0 },
        roro: { wins: 0, losses: 0 },
    };
    let wins = 0;
    let losses = 0;
    let finished = 0;
    for (const g of mine) {
        if (g.gameState.state !== 'game-over')
            continue;
        finished += 1;
        const me = playerIndexOf(g, uid);
        if (g.gameState.winner === me) {
            wins += 1;
            winType.win[g.gameState.winType] += 1;
            byRules[g.rules].wins += 1;
        }
        else {
            losses += 1;
            winType.lose[g.gameState.winType] += 1;
            byRules[g.rules].losses += 1;
        }
    }
    const dice = diceDistribution(collectRollsForUser(mine, uid));
    return {
        uid,
        displayName,
        totalGames: mine.length,
        finishedGames: finished,
        wins,
        losses,
        winRate: finished === 0 ? 0 : wins / finished,
        winType,
        byRules,
        dice,
        diceFairness: chiSquareUniform(dice),
    };
}
function leaderboard(games, users) {
    return users
        .map((u) => {
        const s = computeUserStats(games, u.uid, u.displayName);
        return {
            uid: u.uid,
            displayName: u.displayName,
            games: s.totalGames,
            wins: s.wins,
            losses: s.losses,
            winRate: s.winRate,
        };
    })
        .filter((e) => e.games > 0)
        .sort((x, y) => y.wins - x.wins || y.winRate - x.winRate);
}
function computeGlobalStats(games, users, now) {
    const dice = diceDistribution(collectRolls(games));
    return {
        games: gameDistribution(games),
        dice,
        diceFairness: chiSquareUniform(dice),
        engagement: engagementStats(games, users, now),
        depth: depthStats(games),
        leaderboard: leaderboard(games, users),
    };
}
function engagementStats(games, users, now) {
    const cutoff = now.getTime() - ACTIVE_WINDOW_DAYS * DAY_MS;
    let activeUsers = 0;
    for (const u of users) {
        let lastJoin = 0;
        for (const gameId of Object.keys(u.games)) {
            const g = u.games[gameId];
            if (g)
                lastJoin = Math.max(lastJoin, g.joinedAt.getTime());
        }
        if (lastJoin >= cutoff)
            activeUsers += 1;
    }
    const allowedIds = new Set(games.map((g) => g.gameId));
    const finished = games.filter((g) => g.gameState.state === 'game-over').length;
    return {
        totalUsers: users.length,
        activeUsers,
        signupsByDay: bucketByDay(users.map((u) => u.createdAt)),
        gamesByDay: bucketByDay(gameCreationDates(users, allowedIds)),
        abandonmentRate: games.length === 0 ? 0 : (games.length - finished) / games.length,
    };
}
